import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import { activityTypes } from "../../../../../constants";
import { useRepositoryItems } from "../../../../../hooks/repositoryItems";
import AddNewQuizVersion from "./AddNewQuizVersion";
import AddNewHomeworkVersion from "./AddNewHomeworkVersion";
import { getCourseNames } from "../../../../../utils/course_utils";

/**
 * Container used when adding a new version for an item uses a modal instead of the separate
 * add repository item page. Should be used for quizzes, surveys, homework, and pages
 */
const AddRepositoryItemVersionModal = ({
  open,
  existingItem,
  existingItemId,
  onClose
}) => {
  const [newItem, setNewItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [courseNames, setCourseNames] = useState(null);
  const [newExtendable, setNewExtendable] = useState(null);
  const {
    activateItemVersion,
    createItemVersion,
    deleteItem,
    fetchExtendable,
    fetchItem
  } = useRepositoryItems();

  useEffect(() => {
    if (existingItem && (showQuizModal || showHomeworkModal)) {
      setLoading(true);
      const fetchData = async () => {
        const { id, ...newVersionData } = existingItem;
        let itemVersion, includes;
        if (showQuizModal) {
          itemVersion = await createItemVersion(id, newVersionData);
          includes = [];
        } else {
          itemVersion = existingItem;
          includes = [
            "item",
            "subscribedEmails",
            "fileFormats",
            "homeworkAssignmentAttachments"
          ];
        }

        setNewItem(itemVersion);
        const extendable = await fetchExtendable(itemVersion.id, includes);
        const item = await fetchItem(itemVersion.id, {
          include: "versions,repository,associated_items,associated_courses"
        });
        setCourseNames(getCourseNames(item.associatedCourses));
        setNewExtendable(extendable);
        setLoading(false);
      };
      fetchData();
    }
  }, [open]);

  const itemType = existingItem ? existingItem.extendedItemType : null;
  const isQuizOrSurvey =
    itemType === activityTypes.quiz || itemType === activityTypes.survey;

  const showQuizModal = open && isQuizOrSurvey;

  const isHomework = itemType === activityTypes.homework;

  const showHomeworkModal = open && isHomework;

  const handleClose = async () => {
    await activateItemVersion(existingItemId, newItem.id);
    onClose(newItem.id);
  };

  const handleHomeworkClose = itemId => {
    onClose(itemId);
  };

  const deleteItemVersion = () => {
    if (newItem) {
      deleteItem(newItem.id);
      onClose(existingItemId);
    }
  };

  return (
    <>
      {showQuizModal && (
        <AddNewQuizVersion
          open={showQuizModal}
          onClose={handleClose}
          courseNames={courseNames}
          loading={loading}
          newItem={newItem}
          newQuiz={newExtendable}
          onCancel={deleteItemVersion}
        />
      )}
      {showHomeworkModal && (
        <AddNewHomeworkVersion
          open={showHomeworkModal}
          onClose={handleHomeworkClose}
          courseNames={courseNames}
          loading={loading}
          newHomework={newExtendable}
          item={existingItem}
          onCancel={handleHomeworkClose}
        />
      )}
    </>
  );
};

export default AddRepositoryItemVersionModal;

AddRepositoryItemVersionModal.propTypes = {
  itemType: PropTypes.string,
  itemId: PropTypes.string,
  onClose: PropTypes.func
};
